import { Forum, HighlightOff } from "@mui/icons-material";

import {
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { pink } from "@mui/material/colors";
import { captureException } from "@sentry/react";
import useAppStore from "App/hooks/useAppStore";
import useNotifications from "features/Notifications/hooks/useNotifications";
import { useCallback, useContext, useEffect } from "react";
import ActionButton from "../Buttons/ActionButton";
import FeedbackProviderContext from "./context/FeedbackProviderContext";

interface Props {
  sx?: SxProps;
}

export default function FeedbackProvider({ sx }: Props) {
  const { showModal, setShowModal, feedback, setFeedback } = useContext(
    FeedbackProviderContext,
  );

  const { currentUser } = useAppStore();
  const toggleModal = useCallback(() => setShowModal((prev) => !prev), []);
  const { setNotification } = useNotifications();

  const sendFeedback = useCallback(() => {
    captureException(
      new Error(
        `[Feedback from ${[
          currentUser?.firstName,
          currentUser?.lastName?.charAt(0) || "anonymous",
        ].join(" ")}]: ${feedback}`,
      ),
      { extra: { feedback } },
    );
    setNotification({ message: "Thank you for your feedback!" });
  }, [currentUser, feedback]);

  useEffect(() => {
    if (!showModal) setFeedback(undefined);
  }, [showModal]);

  return (
    <>
      <IconButton onClick={() => setShowModal(!showModal)} sx={sx}>
        <Tooltip title="Provide Feedback" placement="right">
          <Avatar sx={{ bgcolor: pink[500] }}>
            <Forum />
          </Avatar>
        </Tooltip>
      </IconButton>
      <Dialog
        maxWidth="lg"
        onClose={() => setShowModal(false)}
        open={showModal}
      >
        <DialogContent
          sx={{
            padding: "3rem",
            minHeight: "50vh",
            minWidth: "40rem",
            position: "relative",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h4">
              Thank you for providing feedback!
            </Typography>
            <Stack sx={{ opacity: 0.5 }}>
              <Typography>
                If reporting a bug, please include the following:
              </Typography>
              <ul style={{ padding: "0 0 .5rem 1rem" }}>
                <li>Steps to reproduce</li>
                <li>Expected Result</li>
                <li>Actual Result</li>
              </ul>
            </Stack>
            <Stack>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                sx={{ mt: "1rem" }}
              />
              <Typography variant="overline" color="secondary">
                The dev team will also be provided a video of this session with
                relevant debug information
              </Typography>
            </Stack>
            <ActionButton
              onClick={() => {
                sendFeedback();
                toggleModal();
              }}
              disabled={!feedback}
            >
              Provide Feedback
            </ActionButton>
          </Stack>
        </DialogContent>
        <IconButton
          sx={{ position: "absolute", top: ".5rem", right: ".5rem" }}
          size="large"
          color="primary"
          onClick={toggleModal}
        >
          <HighlightOff />
        </IconButton>
      </Dialog>
    </>
  );
}
